// frontend/src/components/Events/AllNewsModal.jsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  useTheme,
  Pagination,
  Stack
} from '@mui/material';
import {
  Close as CloseIcon,
  NewspaperOutlined as NewspaperOutlinedIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import NewsCard from './NewsCard';

const NEWS_PER_PAGE = 12;

/* -------------------------------------------------------------------------- */
/* Glass / iOS-style modal shell                                              */
/* -------------------------------------------------------------------------- */

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': {
    padding: theme.spacing(2)
  },

  '& .MuiDialog-paper': {
    margin: 0,
    width: 'min(760px, calc(100vw - 32px))',
    maxWidth: 760,
    height: 'min(82vh, 820px)',
    maxHeight: 'calc(100vh - 32px)',
    overflow: 'hidden',

    borderRadius: theme.spacing(4),
    border: '1px solid rgba(255,255,255,0.72)',
    background:
      'linear-gradient(145deg, rgba(255,255,255,0.78), rgba(239,246,242,0.64))',
    backdropFilter: 'blur(34px) saturate(165%)',
    WebkitBackdropFilter: 'blur(30px) saturate(150%)',
    boxShadow:
      '0 24px 70px rgba(20,35,28,0.18), 0 4px 18px rgba(20,35,28,0.08)',

    '&::before': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      borderRadius: 'inherit',
      background:
        'linear-gradient(135deg, rgba(255,255,255,0.58), transparent 32%, transparent 70%, rgba(255,255,255,0.22))'
    }
  }
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,

  padding: theme.spacing(2, 2.5, 2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),

  background:
    'linear-gradient(180deg, rgba(255,255,255,0.62), rgba(255,255,255,0.34))',
  borderBottom: '1px solid rgba(255,255,255,0.78)',
  boxShadow: '0 1px 0 rgba(255,255,255,0.55)',

  '&::after': {
    content: '""',
    position: 'absolute',
    left: theme.spacing(2.5),
    right: theme.spacing(2.5),
    bottom: -1,
    height: 1,
    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}55, transparent)`
  }
}));

const HeaderIcon = styled(Box)(({ theme }) => ({
  width: 38,
  height: 38,
  minWidth: 38,
  borderRadius: 13,

  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  color: theme.palette.primary.main,
  background: `linear-gradient(145deg, ${theme.palette.primary.main}18, rgba(255,255,255,0.72))`,
  border: `1px solid ${theme.palette.primary.main}30`,
  boxShadow: `inset 0 1px 0 rgba(255,255,255,0.82), 0 5px 14px ${theme.palette.primary.main}10`
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  width: 38,
  height: 38,
  borderRadius: 13,

  color: theme.palette.text.secondary,
  background: 'rgba(255,255,255,0.58)',
  border: '1px solid rgba(255,255,255,0.82)',

  transition: 'all 0.2s ease',

  '&:hover': {
    color: theme.palette.primary.main,
    background: `rgba(255,255,255,0.82)`,
    borderColor: `${theme.palette.primary.main}38`,
    boxShadow: `0 5px 16px ${theme.palette.primary.main}16`
  }
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  position: 'relative',
  zIndex: 1,

  padding: theme.spacing(0),
  overflow: 'auto',

  background:
    'linear-gradient(180deg, rgba(255,255,255,0.30), rgba(239,246,242,0.22))',

  scrollbarWidth: 'thin',

  '&::-webkit-scrollbar': {
    width: 7
  },

  '&::-webkit-scrollbar-track': {
    background: 'transparent'
  },

  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(100,115,108,0.22)',
    borderRadius: 20
  },

  '&::-webkit-scrollbar-thumb:hover': {
    background: 'rgba(100,115,108,0.34)'
  }
}));

const NewsGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: theme.spacing(2),

  margin: theme.spacing(2.5),
  marginBottom: theme.spacing(2),

  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr'
  },

  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(2),
    margin: theme.spacing(2)
  },

  '& > *': {
    minWidth: 0
  }
}));

const PaginationContainer = styled(Box)(({ theme }) => ({
  position: 'sticky',
  bottom: theme.spacing(1.5),
  zIndex: 5,

  display: 'flex',
  justifyContent: 'center',

  width: 'fit-content',
  maxWidth: 'calc(100% - 32px)',
  margin: theme.spacing(1, 'auto', 2),
  padding: theme.spacing(1, 1.25),

  borderRadius: 18,
  border: '1px solid rgba(255,255,255,0.82)',
  background:
    'linear-gradient(180deg, rgba(255,255,255,0.82), rgba(246,249,247,0.72))',
  backdropFilter: 'blur(24px) saturate(155%)',
  WebkitBackdropFilter: 'blur(24px) saturate(155%)',
  boxShadow:
    '0 10px 28px rgba(20,35,28,0.10), inset 0 1px 0 rgba(255,255,255,0.82)',

  '& .MuiPagination-ul': {
    flexWrap: 'nowrap'
  },

  '& .MuiPaginationItem-root': {
    minWidth: 34,
    height: 34,
    margin: '0 2px',
    borderRadius: 11,

    fontSize: '0.88rem',
    color: theme.palette.text.secondary,
    border: '1px solid transparent',

    transition: 'all 0.18s ease',

    '&:hover': {
      color: theme.palette.primary.main,
      background: `${theme.palette.primary.main}0D`,
      borderColor: `${theme.palette.primary.main}22`
    },

    '&.Mui-selected': {
      color: theme.palette.primary.contrastText,
      background: theme.palette.primary.main,
      borderColor: theme.palette.primary.main,
      boxShadow: `0 4px 12px ${theme.palette.primary.main}28`,

      '&:hover': {
        background: theme.palette.primary.dark
      }
    }
  }
}));

const EmptyState = styled(Box)(({ theme }) => ({
  minHeight: 360,
  margin: theme.spacing(2.5),
  borderRadius: 24,

  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1.25),

  background:
    'linear-gradient(145deg, rgba(255,255,255,0.72), rgba(248,250,249,0.48))',
  border: '1px solid rgba(255,255,255,0.82)',
  boxShadow:
    'inset 0 1px 0 rgba(255,255,255,0.86), 0 8px 24px rgba(20,35,28,0.04)'
}));

const AllNewsModal = ({
  open,
  onClose,
  news = [],
  sections = [],
  likedNews = {},
  onLikeToggle,
  onNewsClick
}) => {
  const theme = useTheme();
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.ceil(news.length / NEWS_PER_PAGE);

  const paginatedNews = news.slice(
    (currentPage - 1) * NEWS_PER_PAGE,
    currentPage * NEWS_PER_PAGE
  );

  const handlePageChange = (event, value) => {
    setCurrentPage(value);

    const contentElement = document.querySelector(
      '.all-news-modal-content'
    );

    if (contentElement) {
      contentElement.scrollTop = 0;
    }
  };

  const handleLikeWrapper = (newsId, e) => {
    e.stopPropagation();
    onLikeToggle(newsId, e);
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      aria-labelledby="all-news-modal-title"
    >
      <StyledDialogTitle id="all-news-modal-title">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            gap: 1.5
          }}
        >
          <HeaderIcon>
            <NewspaperOutlinedIcon fontSize="small" />
          </HeaderIcon>

          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              fontWeight={700}
              noWrap
              sx={{
                lineHeight: 1.15,
                color: theme.palette.text.primary
              }}
            >
              Все новости
            </Typography>

            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.35,
                color: theme.palette.text.secondary
              }}
            >
              {news.length} {news.length === 1 ? 'новость' : 'новостей'}
            </Typography>
          </Box>
        </Box>

        <CloseButton onClick={onClose} aria-label="Закрыть">
          <CloseIcon fontSize="small" />
        </CloseButton>
      </StyledDialogTitle>

      <StyledDialogContent className="all-news-modal-content">
        {news.length === 0 ? (
          <EmptyState>
            <HeaderIcon sx={{ width: 46, height: 46, minWidth: 46 }}>
              <NewspaperOutlinedIcon />
            </HeaderIcon>

            <Typography
              variant="body1"
              fontWeight={600}
              color="text.primary"
            >
              Пока нет новостей
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center' }}
            >
              Здесь появятся новости и материалы событий
            </Typography>
          </EmptyState>
        ) : (
          <>
            <NewsGrid>
              {paginatedNews.map((item) => (
                <NewsCard
                  key={item.id}
                  news={item}
                  sections={sections}
                  isLiked={likedNews[item.id]}
                  onLikeToggle={(e) => handleLikeWrapper(item.id, e)}
                  onClick={() => {
                    onNewsClick(item);
                    onClose();
                  }}
                />
              ))}
            </NewsGrid>

            {totalPages > 1 && (
              <PaginationContainer>
                <Stack>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="medium"
                    showFirstButton
                    showLastButton
                    siblingCount={1}
                    boundaryCount={1}
                    aria-label="Навигация по новостям"
                  />
                </Stack>
              </PaginationContainer>
            )}
          </>
        )}
      </StyledDialogContent>
    </StyledDialog>
  );
};

export default AllNewsModal;
